
import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button, Preloader } from "nlib/ui";
import { getActiveOrganization } from "selectors/organizations";
import { getTextsData } from "selectors/texts";
import { getUserBusinessesData } from "selectors/businesses";
import { getUserData } from "selectors/user";
import { useDispatch, useSelector } from "react-redux";
import BusinessTypes from "const/BusinessTypes";
import IntegrationServices from "const/IntegrationServices";
import React, { useCallback, useEffect, useState } from "react";
import SelectServiceBlock from "./lib/SelectServiceBlock";
import SelectTypeBlock from "./lib/SelectTypeBlock";
import StripeActions from "actions/StripeActions";
import Texts from "utils/Texts";
import classNames from "classnames";
import useAddNewBusiness from "hooks/useAddNewBusiness";
import useShowCommonModal from "hooks/useShowCommonModal";

const STEP_SELECT_RPA = "selectRpa";

const STEP_SELECT_SERVICE = "selectService";

const STEP_COMPLETE = "complete";

const STEPS = [STEP_SELECT_RPA, STEP_SELECT_SERVICE, STEP_COMPLETE];

const FETCHING_DATA_TIMEOUT = 10000;

const AddNewBusiness = ({ onClose }) => {
  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const { appsumoValue, businessesCount, newPrices } = useSelector(getActiveOrganization);

  const businessesData = useSelector(getUserBusinessesData);

  const userData = useSelector(getUserData);

  const addNewBusiness = useAddNewBusiness();

  const showModal = useShowCommonModal();

  const onlyRobotic = appsumoValue || newPrices;

  const [businessType, setBusinessType] = useState(
    onlyRobotic ? BusinessTypes.ROBOTIC : undefined
  );

  const [currentStep, setCurrentStep] = useState(
    onlyRobotic ? STEP_SELECT_SERVICE : STEP_SELECT_RPA
  );

  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (onlyRobotic && currentStep === STEP_SELECT_RPA) {
      setBusinessType(BusinessTypes.ROBOTIC);
      setCurrentStep(STEP_SELECT_SERVICE);
    }
  }, [onlyRobotic, currentStep]);

  const completeSetup = useCallback(async(service, type, price, pricePeriod) => {
    if (!appsumoValue && !price) {
      await showModal({ text: messages.addBusinessPriceUnavailable, headerText: uiTexts.warning });

      return;
    }

    const integrationService = IntegrationServices.getByValue(service);

    const workflow = type === BusinessTypes.ROBOTIC
      ? uiTexts.roboticAiBookkeeper
      : uiTexts.dataEntryAutomationHub;

    const billingNotice = !appsumoValue && Texts.replaceVars(
      businessesCount
        ? messages.addAdditionalBusinessBillingNotice
        : messages.addFirstBusinessBillingNotice,
      { price, pricePeriod }
    );

    const billingConfirmation = !appsumoValue && Texts.replaceVars(
      messages.addBusinessBillingConfirmation,
      { price, pricePeriod }
    );

    const connectionRequirements = type === BusinessTypes.ROBOTIC
      && (service === IntegrationServices.QUICK_BOOKS.value
        ? Texts.replaceVars(messages.onboardingQboRpaConnectRequirements, {
          accessRequirements: businessesData.some(({ extraData = {} }) => {
            return !!extraData.quickBooksAccountantClients && extraData.uncategorizedRpaEnabled === true;
          }) ? "" : `${messages.onboardingQboRpaAccessRequirements.join("\n")}\n`
        })
        : messages.onboardingXeroRpaConnectRequirements);

    const connectionSummary = [
      Texts.replaceVars(messages.addBusinessConnectionConfirm, {
        service: integrationService.label,
        workflow
      }),
      price && !appsumoValue && Texts.replaceVars(messages.addBusinessPrice, { price, pricePeriod })
    ].filter(Boolean).join("\n");

    const modalResult = await showModal({
      text: [
        connectionSummary,
        Texts.replaceVars(messages.addBusinessConnectionRequirements, {
          service: integrationService.label
        }),
        type === BusinessTypes.ROBOTIC
          ? messages.addBusinessRoboticProcessingScope
          : messages.addBusinessRegularProcessingScope,
        connectionRequirements,
        billingNotice,
        billingConfirmation
      ].filter(Boolean).join("\n\n"),
      headerText: uiTexts.connectNewBusiness,
      confirm: true,
      size: "md",
      okButtonText: uiTexts.connect,
      cancelButtonText: uiTexts.back,
      markdown: true
    });

    if (!modalResult) return;
    setFetching(true);
    addNewBusiness({ integrationService: service, rpaMode: type === BusinessTypes.ROBOTIC });
    if (onClose) onClose();
  }, [
    messages,
    uiTexts,
    appsumoValue,
    businessesCount,
    businessesData,
    addNewBusiness,
    showModal,
    onClose
  ]);

  const handleSelectType = useCallback((type) => {
    setBusinessType(type);
    if (currentStep === STEP_SELECT_RPA && userData.holdUser) {
      setFetching(true);
      setTimeout(() => setFetching(false), FETCHING_DATA_TIMEOUT);
      dispatch(StripeActions.createSetupCheckoutSession()).then((sessionUrl) => {
        window.location.href = sessionUrl;
      });
    } else {
      setCurrentStep(STEPS[1]);
    }
  }, [setBusinessType, currentStep, userData.holdUser, dispatch]);

  const handleSelectService = useCallback((service, price, pricePeriod) => {
    completeSetup(service, businessType, price, pricePeriod);
  }, [completeSetup, businessType]);

  const handlePrevClick = useCallback(() => {
    const currentIndex = STEPS.indexOf(currentStep);

    const prevStep = STEPS[currentIndex - 1];

    if (prevStep === STEP_SELECT_RPA && newPrices) {
      if (onClose) onClose();
    } else if (currentIndex) {
      setCurrentStep(prevStep);
    } else if (onClose) onClose();
  }, [currentStep, newPrices, onClose]);

  const showPrevButton = (currentStep !== STEPS[0] || !!onClose)
    && !(newPrices && !onClose && currentStep === STEP_SELECT_SERVICE);

  const showProgress = !newPrices;

  return (
    <div className={Css.addNewBusiness}>
      <div className={Css.container}>
        {showProgress && (
          <div className={Css.progress}>
            {STEPS.map((step) => {
              return <div key={step} className={classNames(Css.bar, currentStep === step && Css.active)} />;
            })}
          </div>
        )}
        <div className={Css.content}>
          {(() => {
            switch (currentStep) {
              case STEP_SELECT_RPA:
                return (
                  <SelectTypeBlock
                    businessType={businessType}
                    onSelectType={handleSelectType} />
                );
              case STEP_SELECT_SERVICE:
                return (
                  <SelectServiceBlock
                    businessType={businessType}
                    onSelectService={handleSelectService} />
                );
              default:
                return <Preloader className={Css.preloader} />;
            }
          })()}
        </div>
        <div className={Css.footer}>
          <div>
            {showPrevButton && (
              <Button
                outline large
                disabled={fetching}
                icon={currentStep === STEPS[0] ? Icons.X : Icons.CaretLeft}
                onClick={handlePrevClick}>
                {currentStep === STEPS[0] ? uiTexts.cancel : uiTexts.back}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AddNewBusiness);
